import * as THREE from 'three';
import { hexDist, hexRing, hexToWorld, type Hex, type MapDef } from '@somnhold/sim';
import { GlowLayer } from '../engine/glow.ts';
import { Kit, place } from '../seasons/kit.ts';
import { looksRng, SkyLook, TAU, type OmenCtx, type OmenScene } from './common.ts';

/**
 * Musgoviejo (docs/LORE.md, «Caras prestadas» in docs/SAGAS.md): the clearing dressed as the village. Low
 * houses of grey stone and dark timber with mossy roofs ring the field between the roads, their windows lit
 * warm and smoke rising from the chimneys; a well in the plaza by the node, fences, washing lines, a cart,
 * woodpiles and lantern posts along the roads. At night (`night`) the sky goes cold and blue under the moon
 * and only the windows and lanterns are warm; nothing hides (that is the bet's night, not this one).
 *
 * Cheap on purpose (docs/RENDIMIENTO.md): the whole village is two merged meshes (the stone, timber and cloth
 * by vertex colour, and the lit windows), one glow layer for every window, lantern and firefly, and smoke from
 * the shared vfx. No lights are added to the scene. Decoration only: the sim knows none of it.
 */

const STONE = ['#6f7178', '#62646b', '#7a7c82'];
const TIMBER = ['#3b2c22', '#33261d', '#45342a'];
const PLASTER = ['#8d877c', '#7f7a70', '#958f84'];
const ROOF = ['#4a4f5a', '#3d424c', '#6e5b3e', '#5c4a33', '#4c5a40'];
const MOSS = ['#46603a', '#3d5634', '#52693f'];
const CLOTH = ['#b9b3a6', '#8e2f2a', '#5d6b7a', '#c9c4b8', '#6d5a46'];
const WINDOW = new THREE.Color('#ffc58a');
const WINDOW_HOT = new THREE.Color('#fff0d8');
const LAMP = new THREE.Color('#ffd6a0');
const FIREFLY = new THREE.Color('#e8ffb0');

const P = (() => {
  const box = new THREE.BoxGeometry(1, 1, 1);
  // A gable: a triangular prism along x, its ridge at y = 1.
  const gable = new THREE.CylinderGeometry(0.5, 0.5, 1, 3, 1).rotateZ(Math.PI / 2).rotateX(-Math.PI / 2);
  gable.computeBoundingBox();
  const bb = gable.boundingBox as THREE.Box3;
  gable.translate(0, -bb.min.y, 0).scale(1, 1 / (bb.max.y - bb.min.y), 1 / (bb.max.z - bb.min.z));
  const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
  const ring = new THREE.CylinderGeometry(0.5, 0.5, 1, 12, 1, true);
  const wheel = new THREE.CylinderGeometry(0.5, 0.5, 0.12, 10).rotateZ(Math.PI / 2);
  const rock = new THREE.DodecahedronGeometry(0.5, 0);
  return { box, gable, cyl, ring, wheel, rock };
})();

const pick = <T>(list: T[], r: number): T => list[Math.floor(r * list.length) % list.length] as T;

/**
 * `square`: El pozo 1's square (engine/well.ts draws the well in the middle and the houses the nightmares go for):
 * no houses of its own here, nor the plaza's well, only the lanterns, the yards' clutter and the night. `plaza`: the
 * plaza's well two hexes from the node (not on El pozo 3's fair night: its stalls stand there, engine/fair.ts).
 */
export const buildVillage = (ctx: OmenCtx, map: MapDef, night: boolean, clear: { x: number; y: number }[] = [], square = false, plaza = !square): OmenScene => {
  const group = new THREE.Group();
  group.name = 'village';
  const low = ctx.low;
  const rnd = looksRng(0x5a11 + map.radius * 31 + map.sectors.reduce((a, s) => a + s.gate.q * 7 + s.gate.r * 13, 0));
  const body = new Kit();
  const lit = new Kit();
  const glows: {
    p: THREE.Vector3;
    c: THREE.Color;
    size: number;
    op: number;
    ph: number;
  }[] = [];
  const chimneys: THREE.Vector3[] = [];

  // Where the roads run (every path hex), to keep the village off them.
  const road: Hex[] = map.sectors.flatMap((s) => s.path);
  const nearRoad = (h: Hex, d: number): boolean => road.some((r) => hexDist(r, h) <= d);
  const world = (h: Hex, lift = 0): THREE.Vector3 => {
    const w = hexToWorld(h.q, h.r);
    return ctx.at(w.x, w.y, lift);
  };
  const yawTo = (p: THREE.Vector3): number => Math.atan2(-p.x, -p.z);

  /** A cottage at `p`, its door facing the plaza (yaw), `s` its size. */
  const house = (p: THREE.Vector3, yaw: number, s: number): void => {
    const g = new THREE.Matrix4().compose(p, new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw), new THREE.Vector3(s, s, s));
    const W = 0.95 + rnd() * 0.25;
    const D = 0.75 + rnd() * 0.15;
    const H = 0.5 + rnd() * 0.12;
    const r = rnd();
    // A stone footing, plaster walls framed in dark timber.
    body.add(P.box, pick(STONE, r), g, place(0, 0.08, 0, 0, new THREE.Vector3(W + 0.06, 0.16, D + 0.06)));
    body.add(P.box, pick(PLASTER, rnd()), g, place(0, 0.16 + H / 2, 0, 0, new THREE.Vector3(W, H, D)));
    const tim = pick(TIMBER, rnd());
    for (const x of [-W / 2, W / 2]) for (const z of [-D / 2, D / 2]) body.add(P.box, tim, g, place(x, 0.16 + H / 2, z, 0, new THREE.Vector3(0.07, H, 0.07)));
    body.add(P.box, tim, g, place(0, 0.16 + H, D / 2 + 0.005, 0, new THREE.Vector3(W, 0.06, 0.04)));
    body.add(P.box, tim, g, place(0, 0.16 + H, -D / 2 - 0.005, 0, new THREE.Vector3(W, 0.06, 0.04)));
    body.add(P.box, tim, g, place(-W / 4, 0.16 + H / 2, D / 2 + 0.01, 0.7, new THREE.Vector3(0.04, H * 1.1, 0.03), 0, 0.7));
    // The door, toward the plaza, with a step.
    body.add(P.box, '#241a14', g, place(W * 0.18, 0.16 + 0.19, D / 2 + 0.012, 0, new THREE.Vector3(0.2, 0.38, 0.03)));
    body.add(P.box, pick(STONE, rnd()), g, place(W * 0.18, 0.05, D / 2 + 0.1, 0, new THREE.Vector3(0.3, 0.06, 0.16)));
    // The roof: a steep gable of dark slate, eaves over the walls, moss along it.
    const RH = 0.5 + rnd() * 0.12;
    const roof = pick(ROOF, rnd());
    body.add(P.gable, roof, g, place(0, 0.16 + H, 0, 0, new THREE.Vector3(W + 0.2, RH, D + 0.26)));
    // A pale ridge cap, so the roof's shape reads from above.
    body.add(P.box, '#8d9099', g, place(0, 0.16 + H + RH - 0.01, 0, 0, new THREE.Vector3(W + 0.24, 0.05, 0.08)));
    for (let i = 0; i < 3; i++) {
      const k = (rnd() - 0.5) * W * 0.8;
      const side = rnd() < 0.5 ? -1 : 1;
      body.add(P.rock, pick(MOSS, rnd()), g, place(k, 0.16 + H + RH * 0.45, side * (D + 0.26) * 0.25, rnd() * TAU, new THREE.Vector3(0.22, 0.06, 0.16), side * 0.9, 0));
    }
    // The chimney, at one end, smoking.
    const cx = (rnd() < 0.5 ? -1 : 1) * W * 0.3;
    body.add(P.box, pick(STONE, rnd()), g, place(cx, 0.16 + H + RH * 0.75, -D * 0.12, 0, new THREE.Vector3(0.13, RH * 0.9, 0.13)));
    chimneys.push(new THREE.Vector3(cx, 0.16 + H + RH * 1.25, -D * 0.12).applyMatrix4(g));
    // Lit windows on the front and the sides: warm squares with a dark frame and a glow.
    const wins: [number, number, number][] = [
      [-W * 0.22, D / 2 + 0.014, 0],
      [W / 2 + 0.014, 0, Math.PI / 2],
      [-W / 2 - 0.014, 0, -Math.PI / 2],
    ];
    for (const [wx, wz, ry] of wins) {
      if (rnd() < 0.25) continue;
      const m = place(wx, 0.16 + H * 0.55, wz, ry, 1);
      body.add(P.box, tim, g, m.clone().multiply(new THREE.Matrix4().makeScale(0.2, 0.2, 0.02)));
      lit.add(
        P.box,
        rnd() < 0.3 ? WINDOW_HOT : WINDOW,
        g,
        m.clone().multiply(new THREE.Matrix4().compose(new THREE.Vector3(0, 0, 0.006), new THREE.Quaternion(), new THREE.Vector3(0.15, 0.15, 0.02))),
      );
      const wp = new THREE.Vector3(0, 0, 0.05).applyMatrix4(m).applyMatrix4(g);
      glows.push({
        p: wp,
        c: WINDOW,
        size: 0.55 * s,
        op: 0.5,
        ph: rnd() * TAU,
      });
      // The light falling out of it onto the ground.
      const gp = new THREE.Vector3(0, 0, 0.45).applyMatrix4(m).applyMatrix4(g);
      gp.y = ctx.ground(gp.x, gp.z) + 0.04;
      glows.push({
        p: gp,
        c: WINDOW,
        size: 1.1 * s,
        op: 0.13,
        ph: rnd() * TAU,
      });
    }
    // Sometimes a fence or a woodpile beside it, a washing line between two posts.
    const extra = rnd();
    if (extra < 0.35) {
      for (let i = 0; i < 4; i++) body.add(P.box, tim, g, place(W / 2 + 0.25, 0.14, -D / 2 + i * (D / 3), 0, new THREE.Vector3(0.05, 0.28, 0.05)));
      for (const y of [0.1, 0.22]) body.add(P.box, tim, g, place(W / 2 + 0.25, y, 0, 0, new THREE.Vector3(0.03, 0.03, D + 0.04)));
    } else if (extra < 0.65) {
      for (let i = 0; i < 6; i++)
        body.add(P.cyl, i % 2 ? '#5a4330' : '#4b3828', g, place(-W / 2 - 0.2, 0.06 + Math.floor(i / 3) * 0.1, -0.15 + (i % 3) * 0.12, 0, new THREE.Vector3(0.1, 0.4, 0.1), 0, Math.PI / 2));
    } else {
      const z = D / 2 + 0.45;
      for (const x of [-W * 0.45, W * 0.45]) body.add(P.box, tim, g, place(x, 0.25, z, 0, new THREE.Vector3(0.04, 0.5, 0.04)));
      body.add(P.box, '#2a2622', g, place(0, 0.48, z, 0, new THREE.Vector3(W * 0.9, 0.008, 0.008)));
      for (let i = 0; i < 3; i++) body.add(P.box, pick(CLOTH, rnd()), g, place(-W * 0.3 + i * W * 0.3, 0.4, z, 0, new THREE.Vector3(0.18, 0.15 + rnd() * 0.06, 0.01), 0, (rnd() - 0.5) * 0.2));
    }
  };

  // The houses: in the rings past the clearing, off the roads, spaced apart, a few nearer between the roads.
  const taken: Hex[] = [];
  const spots: Hex[] = [];
  // Never on a clue (`clear`, in the sim's fixed units): its marker must stay in sight, not under a roof.
  const step = Math.hypot(hexToWorld(1, 0).x - hexToWorld(0, 0).x, hexToWorld(1, 0).y - hexToWorld(0, 0).y);
  const nearClue = (h: Hex): boolean => {
    const w = hexToWorld(h.q, h.r);
    return clear.some((c) => Math.hypot(c.x - w.x, c.y - w.y) < step * 1.6);
  };
  for (let r = 5; r <= Math.min(8, map.radius - 1); r++) for (const h of hexRing(r)) if (!nearRoad(h, 1) && !nearClue(h)) spots.push(h);
  for (let i = spots.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [spots[i], spots[j]] = [spots[j] as Hex, spots[i] as Hex];
  }
  const want = square ? 0 : low ? 9 : 14;
  for (const h of spots) {
    if (taken.length >= want) break;
    if (taken.some((t) => hexDist(t, h) < 2)) continue;
    taken.push(h);
    const p = world(h);
    const jitter = new THREE.Vector3((rnd() - 0.5) * 0.4, 0, (rnd() - 0.5) * 0.4);
    p.add(jitter);
    p.y = ctx.ground(p.x, p.z);
    house(p, yawTo(p) + (rnd() - 0.5) * 0.5, 0.95 + rnd() * 0.25);
  }

  // The well in the plaza: two hexes from the node, as far from the roads' mouths as there is.
  const gates = map.sectors.map((s) => s.gate);
  const wellHex = hexRing(2).reduce((best, h) => (Math.min(...gates.map((g) => hexDist(g, h))) > Math.min(...gates.map((g) => hexDist(g, best))) ? h : best));
  if (plaza) {
    const p = world(wellHex);
    const m = place(p.x, p.y, p.z, yawTo(p), 0.9);
    body.add(P.ring, '#6c6e75', m, place(0, 0.17, 0, 0, new THREE.Vector3(0.62, 0.34, 0.62)));
    body.add(P.ring, '#55575e', m, place(0, 0.17, 0, 0, new THREE.Vector3(0.5, 0.34, 0.5)));
    body.add(P.cyl, '#0c1018', m, place(0, 0.3, 0, 0, new THREE.Vector3(0.5, 0.02, 0.5)));
    body.add(P.box, '#7a7c82', m, place(0, 0.345, 0, 0, new THREE.Vector3(0.66, 0.03, 0.66)));
    for (const x of [-0.27, 0.27]) body.add(P.box, '#3b2c22', m, place(x, 0.5, 0, 0, new THREE.Vector3(0.05, 0.62, 0.05)));
    body.add(P.box, '#3b2c22', m, place(0, 0.76, 0, 0, new THREE.Vector3(0.62, 0.04, 0.04)));
    body.add(P.gable, '#3b3f47', m, place(0, 0.8, 0, 0, new THREE.Vector3(0.78, 0.22, 0.42)));
    body.add(P.cyl, '#4b3828', m, place(0.06, 0.55, 0, 0, new THREE.Vector3(0.1, 0.1, 0.1)));
    body.add(P.box, '#2a2622', m, place(0.06, 0.66, 0, 0, new THREE.Vector3(0.006, 0.2, 0.006)));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU + rnd();
      body.add(P.rock, pick(STONE, rnd()), m, place(Math.cos(a) * 0.55, 0.03, Math.sin(a) * 0.55, rnd() * TAU, 0.12));
    }
  }

  // Lantern posts along each road, every other hex, on alternating sides; their pools of light on the road.
  for (const s of map.sectors) {
    const path = s.path;
    for (let i = 1; i < path.length - 1; i += 2) {
      const a = world(path[i] as Hex);
      const b = world(path[i + 1] as Hex);
      const dir = b.clone().sub(a).setY(0).normalize();
      const side = i % 4 === 1 ? 1 : -1;
      const p = a.clone().add(new THREE.Vector3(-dir.z, 0, dir.x).multiplyScalar(0.62 * side));
      p.y = ctx.ground(p.x, p.z);
      const m = place(p.x, p.y, p.z, Math.atan2(dir.x, dir.z));
      body.add(P.box, '#2c2620', m, place(0, 0.45, 0, 0, new THREE.Vector3(0.05, 0.9, 0.05)));
      body.add(P.box, '#2c2620', m, place(0, 0.88, 0.1, 0, new THREE.Vector3(0.03, 0.03, 0.22)));
      body.add(P.box, '#1d1b19', m, place(0, 0.8, 0.2, 0, new THREE.Vector3(0.1, 0.03, 0.1)));
      lit.add(P.box, LAMP, m, place(0, 0.73, 0.2, 0, new THREE.Vector3(0.07, 0.11, 0.07)));
      const lp = new THREE.Vector3(0, 0.73, 0.2).applyMatrix4(m);
      glows.push({ p: lp, c: LAMP, size: 0.7, op: 0.7, ph: rnd() * TAU });
      glows.push({
        p: new THREE.Vector3(lp.x, ctx.ground(lp.x, lp.z) + 0.04, lp.z),
        c: LAMP,
        size: 2.0,
        op: 0.12,
        ph: rnd() * TAU,
      });
    }
  }

  // A cart left by a house, with sacks; a haystack; barrels by a door.
  const props = spots.filter((h) => !taken.some((t) => hexDist(t, h) < 1)).slice(0, low ? 3 : 6);
  props.forEach((h, i) => {
    const p = world(h);
    p.x += (rnd() - 0.5) * 0.5;
    p.z += (rnd() - 0.5) * 0.5;
    p.y = ctx.ground(p.x, p.z);
    const m = place(p.x, p.y, p.z, rnd() * TAU);
    if (i % 3 === 0) {
      body.add(P.box, '#4a3628', m, place(0, 0.26, 0, 0, new THREE.Vector3(0.45, 0.06, 0.75)));
      for (const x of [-0.22, 0.22]) body.add(P.box, '#3b2c22', m, place(x, 0.34, 0, 0, new THREE.Vector3(0.03, 0.12, 0.75)));
      for (const x of [-0.27, 0.27]) body.add(P.wheel, '#2c2620', m, place(x, 0.17, 0.05, 0, new THREE.Vector3(0.34, 0.34, 0.34)));
      body.add(P.box, '#3b2c22', m, place(0, 0.2, 0.62, 0, new THREE.Vector3(0.04, 0.04, 0.5), 0.25));
      for (let k = 0; k < 3; k++) body.add(P.rock, '#7d7462', m, place((k - 1) * 0.12, 0.36, -0.1 + (k % 2) * 0.2, rnd() * TAU, new THREE.Vector3(0.2, 0.15, 0.24)));
    } else if (i % 3 === 1) {
      for (let k = 0; k < 3; k++) body.add(P.box, k % 2 ? '#5a4330' : '#6b5038', m, place((k % 2) * 0.32 - 0.16, 0.13 + (k === 2 ? 0.26 : 0), 0, rnd() * 0.3, 0.26));
    } else {
      for (let k = 0; k < 3; k++) body.add(P.cyl, '#4b3828', m, place((k - 1) * 0.26, 0.17, 0, 0, new THREE.Vector3(0.22, 0.34, 0.22)));
    }
  });

  const vmat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.92,
    metalness: 0,
    flatShading: true,
  });
  const wmat = new THREE.MeshBasicMaterial({
    vertexColors: true,
    toneMapped: false,
  });
  const bodyMesh = body.build(vmat, 'village-body');
  const litMesh = lit.build(wmat, 'village-windows');
  if (bodyMesh) {
    bodyMesh.castShadow = false;
    bodyMesh.receiveShadow = true;
    group.add(bodyMesh);
  }
  if (litMesh) group.add(litMesh);
  const glow = new GlowLayer(256);
  group.add(glow.mesh);

  // Fireflies over the gardens (none on low quality).
  const flies = low
    ? []
    : Array.from({ length: 22 }, () => {
        const h = taken[Math.floor(rnd() * taken.length)] ?? wellHex;
        const p = world(h, 0.4);
        return {
          o: p,
          r: 0.4 + rnd() * 0.7,
          sp: 0.2 + rnd() * 0.4,
          ph: rnd() * TAU,
        };
      });

  // At night: the cold of the moon over the village (lights only; every nightmare still shows).
  const sky = night
    ? new SkyLook(ctx, {
        hemi: ['#5d6f94', '#1c2230', 0.62],
        key: ['#a9bfe8', 0.62],
        rim: ['#86a4dc', 1.15],
        background: '#0e1422',
        fog: '#161d2c',
        mist: { low: '#1a2030', mid: '#141a28', far: '#0c1018', alpha: 1.1 },
      })
    : null;

  let lastSmoke = 0;
  return {
    group,
    frame: (t: number) => {
      sky?.pulse({});
      glow.begin();
      for (const g of glows) glow.add(g.p.x, g.p.y, g.p.z, g.c, g.size, g.op * (0.88 + 0.12 * Math.sin(t * 3.1 + g.ph)));
      for (const f of flies) {
        const blink = Math.max(0, Math.sin(t * 1.7 + f.ph * 3));
        glow.add(f.o.x + Math.cos(t * f.sp + f.ph) * f.r, f.o.y + Math.sin(t * f.sp * 1.7 + f.ph) * 0.18, f.o.z + Math.sin(t * f.sp + f.ph) * f.r, FIREFLY, 0.18, 0.85 * blink);
      }
      glow.end();
      // Smoke from the chimneys, a puff now and then (fewer on low quality).
      if (t - lastSmoke > (low ? 1.2 : 0.45)) {
        lastSmoke = t;
        const c = chimneys[Math.floor((t * 7.3) % Math.max(1, chimneys.length))];
        if (c)
          ctx.vfx.burst(c, {
            n: 1,
            color: night ? '#3a4150' : '#8a8a8e',
            speed: 0.004,
            up: 0.012,
            size: 0.45,
            life: 40,
            smoke: true,
            gravity: -0.0004,
            spread: 0.05,
          });
      }
    },
    dispose: () => {
      sky?.restore();
      bodyMesh?.geometry.dispose();
      litMesh?.geometry.dispose();
      vmat.dispose();
      wmat.dispose();
      glow.mesh.geometry.dispose();
      glow.material.dispose();
    },
  };
};
